import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Stack, Button,
  FormGroup, FormControlLabel, Checkbox, Divider, Alert, CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useSnackbar } from 'notistack';
import PageHeader from '../components/PageHeader';
import PermissionGate from '../components/PermissionGate';
import api from '../api';

// Assign product roles to a user within an organization (user_companies + user_company_roles), then
// PUBLISH the resulting product permissions to the gateway via permission-sync — the bridge from the
// product's role model to the gateway's product_permissions (keyed user_id#organization_id#product_id).
const UserRolesManager = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userId, setUserId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, c, r] = await Promise.all([
          api.get('/users'),
          api.get('/user-roles/companies'),
          api.get('/roles')
        ]);
        setUsers(u.data.users);
        setCompanies(c.data.companies);
        setRoles(r.data.roles);
      } catch (err) {
        enqueueSnackbar(err.response?.data?.message || 'Failed to load data', { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  const loadAssignment = useCallback(async () => {
    if (!userId || !companyId) { setSelected([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/user-roles', { params: { userId, companyId } });
      setSelected(data.roleIds);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to load assignment', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [userId, companyId, enqueueSnackbar]);

  useEffect(() => { loadAssignment(); }, [loadAssignment]);

  const toggle = (rid) =>
    setSelected((s) => (s.includes(rid) ? s.filter((x) => x !== rid) : [...s, rid]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/user-roles', { userId, companyId, roleIds: selected });
      enqueueSnackbar('Roles saved', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post('/user-roles/publish', { userId, companyId });
      const modules = Object.keys(data.permissions || {});
      enqueueSnackbar(
        `Published ${data.action} to gateway (${modules.length} module${modules.length === 1 ? '' : 's'})`,
        { variant: 'success' }
      );
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Publish failed', { variant: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  const ready = Boolean(userId && companyId);

  return (
    <Box>
      <PageHeader
        title="User roles"
        subtitle="Assign product roles to a user within an organization, then publish the resulting permissions to the gateway."
      />

      <Card>
        <CardContent>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
            <TextField
              select label="User" value={userId} sx={{ minWidth: 280 }}
              onChange={(e) => setUserId(e.target.value)}
            >
              {users.map((u) => (
                <MenuItem key={u.user_id} value={u.user_id}>
                  {u.name || u.email} — {u.email}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select label="Organization" value={companyId} sx={{ minWidth: 240 }}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              {companies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name} — org {c.organization_id || '—'}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {!ready && (
            <Alert severity="info" sx={{ mt: 3 }}>
              Pick a user and an organization to manage their roles.
            </Alert>
          )}

          {ready && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Roles in this organization</Typography>
              {loading ? (
                <CircularProgress size={22} sx={{ my: 2 }} />
              ) : roles.length ? (
                <FormGroup>
                  {roles.map((r) => (
                    <FormControlLabel
                      key={r.id}
                      control={<Checkbox checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />}
                      label={`${r.display_name} (${r.name})`}
                    />
                  ))}
                </FormGroup>
              ) : (
                <Typography variant="body2" color="text.disabled">No roles defined. Create some on the Roles page.</Typography>
              )}

              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving || loading}>
                  Save roles
                </Button>
                <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={publish} disabled={publishing || loading}>
                  Publish permissions to gateway
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                Publish derives this user's product permissions from their roles and sends them to the gateway
                (permission-sync). They then appear under <code>product_permissions</code> in the entitlement profile.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const UserRoles = () => (
  <PermissionGate permission="users:manage">
    <UserRolesManager />
  </PermissionGate>
);

export default UserRoles;
